import { Globe2, ScrollText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { api, isApiError } from '../../api';
import type { ContactCommand, ContactDetail, ContactDomain } from '../../api/areas/core';
import { Badge } from '../../components/Badge';
import { DataTable, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { CorrelationLink } from '../../components/Ids';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { addressLine } from '../../lib/address';
import { formatDate, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { CustomerRef, DomainRef, EppResult, RegistryRef, RoleBadge, SubjectRef, TxRef, WorkflowRef } from '../domains/refs';
import { COMMAND_STATE, humanize, LIFECYCLE, StatusBadge } from '../domains/status';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

/** `/contacts/:registry/:handle`: one registry contact object, who it belongs to and where it is used. */
export function ContactPage() {
  const { t } = useTranslation('domains');
  const tr = t as unknown as Lookup;
  const { registry = '', handle = '' } = useParams();
  useDocumentTitle(t('contacts.detailTitle', { handle }));
  const result = useApi((signal) => api.getContact(registry, handle, { signal }), [registry, handle]);
  const crumbs = [{ label: t('contacts.crumb') }, { label: t('contacts.title'), to: '/contacts' }, { label: handle }];

  if (result.status === 'loading') {
    return (
      <>
        <PageHeader title={handle} crumbs={crumbs} />
        <Loading rows={4} />
      </>
    );
  }
  if (result.status === 'error') {
    const notFound = isApiError(result.error) && result.error.status === 404;
    return (
      <>
        <PageHeader title={handle} crumbs={crumbs} />
        <DataError error={result.error} onRetry={notFound ? undefined : result.reload} title={notFound ? t('contacts.notFound') : t('contacts.loadDetailFailed')} />
      </>
    );
  }
  const c = result.data;
  const s = c.subject;

  const domainColumns: Column<ContactDomain>[] = [
    { key: 'domain', header: t('contacts.domain'), cell: (d) => <DomainRef ascii={d.ascii_name} unicode={d.unicode_name} id={d.domain_id} tab="contacts" /> },
    {
      key: 'role',
      header: t('contacts.role'),
      cell: (d) => (
        <div className="flex flex-col items-start gap-1">
          <RoleBadge role={d.role} />
          {d.valid_until ? (
            <Badge tone="neutral" icon={false}>
              {t('common.ended')}
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'period',
      header: t('contacts.period'),
      cell: (d) => (
        <div className="text-xs whitespace-nowrap">
          <div>{t('common.since', { when: formatDate(d.valid_from) })}</div>
          {d.valid_until ? <div className="text-muted-foreground">{t('common.until', { when: formatDate(d.valid_until) })}</div> : null}
        </div>
      ),
    },
    { key: 'customer', header: t('contacts.customer'), cell: (d) => <CustomerRef id={d.customer_id} name={d.customer_name} /> },
    { key: 'lifecycle', header: t('contacts.lifecycle'), cell: (d) => <StatusBadge map={LIFECYCLE} value={d.lifecycle_state} /> },
    { key: 'expiry', header: t('contacts.expiry'), className: 'whitespace-nowrap', cell: (d) => formatDate(d.registry_expires_at) },
  ];

  const commandColumns: Column<ContactCommand>[] = [
    { key: 'started', header: t('epp.started'), className: 'whitespace-nowrap', cell: (x) => formatDateTime(x.started_at) },
    { key: 'op', header: t('epp.command'), cell: (x) => <TxRef id={x.id} operation={x.operation} sub={x.attempt > 1 ? t('registry.attempt', { n: x.attempt }) : undefined} /> },
    { key: 'state', header: t('registry.state'), cell: (x) => <StatusBadge map={COMMAND_STATE} value={x.state} /> },
    {
      key: 'result',
      header: t('epp.result'),
      cell: (x) =>
        x.result_code !== null ? (
          <EppResult code={x.result_code} message={x.result_message} />
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    { key: 'trid', header: 'clTRID', cell: (x) => <CopyText value={x.cltrid} /> },
    {
      key: 'wf',
      header: t('epp.workflow'),
      cell: (x) => (
        <div className="flex flex-col gap-1 text-xs">
          <WorkflowRef id={x.workflow_id} type={x.workflow_type} sub={null} />
          {x.correlation_id ? <CorrelationLink id={x.correlation_id} label={t('registry.correlation')} /> : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={<span className="font-mono">{c.handle}</span>}
        crumbs={crumbs}
        subtitle={t('contacts.detailSubtitle', { registry: c.registry_name })}
        actions={
          c.roles.length ? (
            <span className="flex flex-wrap gap-1">
              {c.roles.map((r) => (
                <RoleBadge key={r} role={r} />
              ))}
            </span>
          ) : (
            <Badge tone="neutral">{t('contacts.noRoles')}</Badge>
          )
        }
      />
      <div className="grid grid-cols-1 gap-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Section title={t('contacts.object')}>
            <KeyValue
              columns={2}
              rows={[
                [t('contacts.handle'), <CopyText key="h" value={c.handle} />],
                [t('contacts.registry'), <RegistryRef key="r" code={c.registry_code} name={c.registry_name} />],
                [t('contacts.domainCount'), c.total_domain_count !== c.domain_count ? `${c.domain_count} (${t('contacts.totalDomains', { count: c.total_domain_count })})` : c.domain_count],
                [t('contacts.firstUsed'), formatDate(c.first_used_at)],
                [t('contacts.lastAssigned'), formatDate(c.last_assigned_at)],
              ]}
            />
          </Section>
          <Section title={t('contacts.behind')} description={s ? t('contacts.behindDescription') : undefined}>
            {s ? (
              <KeyValue
                columns={2}
                rows={[
                  [t('contacts.subjectName'), <SubjectRef key="s" id={s.subject_id} name={s.display_name} type={s.subject_type} />],
                  [t('contacts.subjectType'), tr(`subject.types.${s.subject_type}`, { defaultValue: humanize(s.subject_type) })],
                  [
                    t('contacts.subjectVersion'),
                    `${t('common.version', { n: s.version_number })}${s.is_current_version ? '' : ` · ${t('contacts.notCurrentVersion')}`}`,
                  ],
                  [
                    t('contacts.verification'),
                    s.verification_status ? (
                      <Badge key="v" tone={s.verification_status === 'VERIFIED' ? 'ok' : 'pending'} title={s.verification_status}>
                        {tr(`subject.verificationStatuses.${s.verification_status}`, { defaultValue: humanize(s.verification_status) })}
                      </Badge>
                    ) : (
                      '—'
                    ),
                  ],
                  [t('contacts.country'), s.country_code ?? '—'],
                  [t('contacts.managingCustomer'), <CustomerRef key="c" id={s.managing_customer_id} name={s.managing_customer_name} />],
                ]}
              />
            ) : (
              <p className="text-sm text-muted-foreground">{t('contacts.noSubjectBody')}</p>
            )}
          </Section>
        </div>

        <ContactDataSection c={c} />

        <Section flush title={t('contacts.domains')}>
          <DataTable columns={domainColumns} rows={c.domains} rowKey={(d) => d.id} empty={<EmptyState title={t('contacts.empty')} icon={Globe2} />} />
        </Section>

        <Section flush title={t('contacts.commands')}>
          {c.registry_transactions === null ? (
            <p className="px-5 pb-5 text-sm text-muted-foreground">{t('contacts.commandsHidden')}</p>
          ) : (
            <DataTable
              columns={commandColumns}
              rows={c.registry_transactions}
              rowKey={(x) => x.id}
              empty={<EmptyState title={t('contacts.noCommands')} icon={ScrollText} />}
            />
          )}
        </Section>
      </div>
    </>
  );
}

/** The data the registry holds for this contact, as the platform sent it. */
function ContactDataSection({ c }: { c: ContactDetail }) {
  const { t } = useTranslation('domains');
  const tr = t as unknown as Lookup;
  const d = c.details;
  if (!c.subject) return null;
  if (!d) {
    return (
      <Section title={t('contacts.data.title')}>
        <p className="text-sm text-muted-foreground">{t('contacts.data.hidden')}</p>
      </Section>
    );
  }
  const addr = d.vn_address
    ? d.vn_address.full_address
    : d.address
      ? addressLine(d.address)
      : null;
  const dash = (v: string | null | undefined) => (v && v.trim() ? v : '—');
  return (
    <Section title={t('contacts.data.title')} description={t('contacts.data.description')}>
      <KeyValue
        columns={2}
        rows={[
          [t('contacts.data.name'), d.name],
          ...(d.organization ? ([[t('contacts.data.organization'), d.organization]] as [string, string][]) : []),
          ...(d.trade_name ? ([[t('contacts.data.tradeName'), d.trade_name]] as [string, string][]) : []),
          [t('contacts.data.kind'), tr(`subject.types.${d.kind}`, { defaultValue: humanize(d.kind) })],
          [t('contacts.data.email'), d.email ? <CopyText key="e" value={d.email} /> : '—'],
          [t('contacts.data.phone'), d.phone ? <CopyText key="p" value={d.phone} /> : '—'],
          [t('contacts.data.fax'), dash(d.fax)],
          [t('contacts.data.gender'), d.gender ? tr(`contacts.data.genders.${d.gender}`, { defaultValue: d.gender }) : '—'],
          [t('contacts.data.position'), dash(d.position)],
          [t('contacts.data.nationality'), dash(d.nationality)],
          [t('contacts.data.birthDate'), d.has_birth_date ? t('contacts.data.onFile') : '—'],
          [
            t('contacts.data.identity'),
            d.identifiers.length
              ? d.identifiers.map((i) => `${tr(`subject.identifierTypes.${i.type}`, { defaultValue: humanize(i.type) })} ${i.hint ?? ''}`.trim()).join(' · ')
              : '—',
          ],
          [t('contacts.data.address'), dash(addr)],
          ...(d.vn_address
            ? ([
                [t('contacts.data.province'), `${d.vn_address.province_name} (${d.vn_address.province_id})`],
                [t('contacts.data.ward'), d.vn_address.ward_name ? `${d.vn_address.ward_name} (${d.vn_address.ward_id ?? '—'})` : '—'],
              ] as [string, string][])
            : []),
        ]}
      />
    </Section>
  );
}
